import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { BUILDING_TYPES, BUILDING_STATES, buildingIcon, typeOf, stateOf } from './buildingIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const notifyChange = () => window.dispatchEvent(new Event('civis-buildings-changed'));

export const useBuildings = () => {
    const [buildings, setBuildings] = useState([]);
    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/map/buildings`, { headers: headers() });
            setBuildings(res.data);
        } catch { /* carte sans reperes */ }
    }, []);
    useEffect(() => {
        load();
        const timer = setInterval(load, 30000);
        window.addEventListener('civis-buildings-changed', load);
        return () => { clearInterval(timer); window.removeEventListener('civis-buildings-changed', load); };
    }, [load]);
    return [buildings, setBuildings];
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

const BuildingPopup = ({ b }) => (
    <div className="text-xs min-w-[180px]" data-testid={`building-popup-${b.id}`}>
        <div className="font-semibold">{typeOf(b.building_type).label}</div>
        <span className="inline-block px-1.5 py-0.5 my-1 text-white font-semibold" style={{ background: stateOf(b.state).color, border: '1px solid #ffffff' }}>{stateOf(b.state).label}</span>
        {b.address && <div>{b.address}</div>}
        <div>Occupants : {b.occupants || 0}</div>
        {b.remarks && <div className="mt-1">{b.remarks}</div>}
        <div className="opacity-60 mt-1">MAJ {fmt(b.updated_at)} — {b.updated_by}</div>
    </div>
);

const reverseGeocode = async (lat, lng) => {
    try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=fr`);
        const data = await res.json();
        return data.display_name || '';
    } catch {
        return '';
    }
};

const inputCls = 'w-full border border-zinc-300 bg-white text-zinc-900 px-2 py-1 text-xs';

const BuildingForm = ({ form, setForm, onSave, onDelete, onCancel, saving }) => (
    <div className="space-y-2" data-testid="building-form">
        <p className="text-xs font-semibold text-zinc-900">{form.id ? 'Modifier le repère' : 'Nouveau repère'}</p>
        <select className={inputCls} value={form.building_type} onChange={(e) => setForm({ ...form, building_type: e.target.value })} data-testid="building-form-type">
            {BUILDING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select className={inputCls} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="building-form-state">
            {BUILDING_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <input className={inputCls} placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="building-form-address" />
        <label className="block text-[11px] text-zinc-700">Nombre d'occupants
            <input type="number" min="0" className={inputCls} value={form.occupants} onChange={(e) => setForm({ ...form, occupants: e.target.value })} data-testid="building-form-occupants" />
        </label>
        <textarea className={inputCls} rows={2} placeholder="Remarques" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="building-form-remarks" />
        <div className="flex gap-2">
            <button type="button" disabled={saving} onClick={onSave} className="flex-1 bg-blue-700 text-white text-xs py-1.5" data-testid="building-form-save">Enregistrer</button>
            <button type="button" onClick={onCancel} className="border border-zinc-300 text-zinc-800 text-xs px-2" data-testid="building-form-cancel">Annuler</button>
        </div>
        {form.id && <button type="button" onClick={onDelete} className="w-full text-xs text-red-700 underline" data-testid="building-form-delete">Supprimer ce repère</button>}
    </div>
);

const Palette = ({ placing, setPlacing }) => (
    <div className="space-y-1">
        {BUILDING_TYPES.map(({ value, label, Icon }) => (
            <button key={value} type="button" onClick={() => setPlacing(placing === value ? null : value)}
                className={`w-full flex items-center gap-2 px-2 py-1 text-xs border ${placing === value ? 'border-blue-700 bg-blue-50 text-blue-900' : 'border-transparent text-zinc-800 hover:bg-zinc-100'}`}
                data-testid={`building-place-${value}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
            </button>
        ))}
        {placing && <p className="text-[11px] text-blue-900" data-testid="building-place-hint">Cliquez sur la carte pour placer le repère.</p>}
        <div className="pt-2 border-t border-zinc-200 space-y-0.5">
            {BUILDING_STATES.map((s) => (
                <p key={s.value} className="flex items-center gap-1.5 text-[11px] text-zinc-700">
                    <span className="w-2.5 h-2.5 inline-block" style={{ background: s.color, outline: '1px solid #b9c0cc' }} /> {s.label}
                </p>
            ))}
        </div>
    </div>
);

const EMPTY = { building_type: 'maison', state: 'intact', address: '', occupants: 0, remarks: '' };

const BuildingsEditor = ({ buildings, setBuildings }) => {
    const map = useMap();
    const panelRef = useRef(null);
    const [placing, setPlacing] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const visible = open || !!form;

    useEffect(() => {
        const toggle = () => { setOpen((o) => !o); setPlacing(null); };
        window.addEventListener('civis-building-panel-toggle', toggle);
        return () => window.removeEventListener('civis-building-panel-toggle', toggle);
    }, []);

    useEffect(() => {
        if (!panelRef.current) return;
        L.DomEvent.disableClickPropagation(panelRef.current);
        L.DomEvent.disableScrollPropagation(panelRef.current);
    }, [visible]);

    useEffect(() => {
        map.getContainer().style.cursor = placing ? 'crosshair' : '';
    }, [placing, map]);

    useMapEvents({
        click: async (e) => {
            if (!placing) return;
            const { lat, lng } = e.latlng;
            setForm({ ...EMPTY, building_type: placing, latitude: lat, longitude: lng });
            setPlacing(null);
            const address = await reverseGeocode(lat, lng);
            setForm((f) => (f && !f.id && !f.address ? { ...f, address } : f));
        },
    });

    const save = async () => {
        setSaving(true);
        const payload = { ...form, occupants: Number(form.occupants) || 0 };
        try {
            const res = form.id
                ? await axios.put(`${API}/map/buildings/${form.id}`, payload, { headers: headers() })
                : await axios.post(`${API}/map/buildings`, payload, { headers: headers() });
            const saved = { ...payload, ...res.data, updated_at: res.data.updated_at || new Date().toISOString() };
            setBuildings((prev) => [...prev.filter((b) => b.id !== form.id), saved]);
            setForm(null);
            setOpen(false);
            if (!res.data._offline) { toast.success('Repère enregistré'); notifyChange(); }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm('Supprimer ce repère ?')) return;
        try {
            await axios.delete(`${API}/map/buildings/${form.id}`, { headers: headers() });
            setBuildings((prev) => prev.filter((b) => b.id !== form.id));
            setForm(null);
            toast.success('Repère supprimé');
            notifyChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur de suppression');
        }
    };

    const move = async (b, e) => {
        const { lat, lng } = e.target.getLatLng();
        try {
            await axios.put(`${API}/map/buildings/${b.id}`, { latitude: lat, longitude: lng }, { headers: headers() });
            setBuildings((prev) => prev.map((x) => (x.id === b.id ? { ...x, latitude: lat, longitude: lng } : x)));
        } catch { toast.error('Erreur de déplacement'); }
    };

    return (
        <>
            {buildings.map((b) => (
                <Marker key={`bld-${b.id}`} position={[b.latitude, b.longitude]} icon={buildingIcon(b.building_type, b.state)}
                    draggable pmIgnore
                    eventHandlers={{ click: () => setForm({ ...EMPTY, ...b }), dragend: (e) => move(b, e) }} />
            ))}
            {visible && (
                <div className="leaflet-top leaflet-left" style={{ marginTop: 90, pointerEvents: 'auto' }}>
                    <div ref={panelRef} className="leaflet-control bg-white border border-zinc-300 p-2 w-56" data-testid="building-tools-panel">
                        <div className="flex items-center justify-between mb-1.5">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Repères bâtiments</p>
                            <button type="button" onClick={() => { setOpen(false); setForm(null); setPlacing(null); }}
                                className="text-zinc-600 text-xs px-1" title="Fermer" data-testid="building-panel-close"><X className="w-3.5 h-3.5" /></button>
                        </div>
                        {form
                            ? <BuildingForm form={form} setForm={setForm} onSave={save} onDelete={remove} onCancel={() => setForm(null)} saving={saving} />
                            : <Palette placing={placing} setPlacing={setPlacing} />}
                    </div>
                </div>
            )}
        </>
    );
};

// Reperes de batiments : lecture seule, ou edition (placement, etat, deplacement) pour les profils qui dessinent
const BuildingsLayer = ({ editable = false }) => {
    const [buildings, setBuildings] = useBuildings();
    if (editable) return <BuildingsEditor buildings={buildings} setBuildings={setBuildings} />;
    return buildings.map((b) => (
        <Marker key={`bld-${b.id}`} position={[b.latitude, b.longitude]} icon={buildingIcon(b.building_type, b.state)} pmIgnore>
            <Popup><BuildingPopup b={b} /></Popup>
        </Marker>
    ));
};

export default BuildingsLayer;
